import Chart, {ArgumentAxis, CommonSeriesSettings, Legend, Point, Series, Size, Strip, Tooltip, ValueAxis, Title} from 'devextreme-react/chart'
import DateBox from 'devextreme-react/date-box'
import {Popup} from 'devextreme-react'
import React, {useState} from 'react'
import {getEnergosferaCounter} from '../../api/point'
import {Button} from 'devextreme-react/button'
import {PopupTitleWithIcon} from '../popup-title-with-icon/popup-title-with-icon'
import LoadPanel from 'devextreme-react/load-panel'
import notify from 'devextreme/ui/notify'
import {useAuth} from '../../contexts/auth'
import {periodDayKey, shiftEnergosferaHourLeft} from '../../utils/date.utils'
import {exportConsumptionExcel, stampPeakFlags} from '../../utils/consumption-excel'
import {
    FACT_MARK,
    PLAN_MARK,
    PeakMarksCaption,
    hasPeakMark,
    holidayStrips,
    loadPeakOverlay,
    slotParts,
    withPeakMarks,
} from '../../utils/peak-chart-marks'

const calendarOptions = {
    applyValueMode: 'useButtons',
    showDropDownButton: true,
}

function slotKey(row) {
    return String(row.dat_local || row.time || row.format_time || '')
}

function sortStamp(s) {
    const m = String(s).match(/(\d{2})\.(\d{2})\.(\d{4})\s+(\d{2}):(\d{2})/)
    if (!m) return String(s)
    return `${m[3]}${m[2]}${m[1]}${m[4]}${m[5]}`
}

/**
 * Сумма почасовых профилей Энергосферы по code_fact договора.
 */
export default function EnergosferaChartDog({visible, onClosed, codes = [], period1: initial1, period2: initial2}) {
    const {selectedDog} = useAuth()
    const [period1, setPeriod1] = useState(initial1 || new Date())
    const [period2, setPeriod2] = useState(initial2 || new Date())
    const [data, setData] = useState([])
    const [strips, setStrips] = useState([])
    const [loading, setLoading] = useState(false)
    const [excel, setExcel] = useState(null)

    async function refreshClick() {
        if (!codes.length) {
            notify('По договору нет фактических кодов каналов', 'warning', 4000)
            return
        }
        setLoading(true)
        setExcel(null)
        const loadedPeriod1 = period1
        const loadedPeriod2 = period2
        const buckets = new Map()
        const overlayPromise = loadPeakOverlay(selectedDog?.kodDog, period1, period2)
        try {
            for (const code of codes) {
                const res = await getEnergosferaCounter(null, period1, period2, 'hour', code)
                if (!res.isOk) {
                    notify(res.message || `Нет профиля по коду ${code}`, 'warning', 4000)
                    continue
                }
                for (const row of res.data?.reads || []) {
                    const shifted = shiftEnergosferaHourLeft(slotKey(row))
                    if (!shifted) continue
                    if (shifted.dayKey < periodDayKey(period1) || shifted.dayKey > periodDayKey(period2)) continue
                    const key = shifted.dat_local
                    const prev = buckets.get(key) || {dat_local: key, dat_out: shifted.dat_out, value: 0}
                    prev.value += Number(row.valueExcell ?? row.value) || 0
                    buckets.set(key, prev)
                }
            }
            const rows = [...buckets.values()].sort((a, b) => sortStamp(a.dat_local).localeCompare(sortStamp(b.dat_local)))
            const overlay = await overlayPromise
            const marked = withPeakMarks(rows, overlay)
            setData(marked)
            setStrips(holidayStrips(marked, overlay.holidays, 'dat_local'))
            if (rows.length) {
                const dogSeries = rows.map((r) => ({dat_out: r.dat_out, out: r.value, new_power: null, old_power: null}))
                stampPeakFlags(dogSeries, overlay.series)
                setExcel({dogSeries, holidays: overlay.holidays, period1: loadedPeriod1, period2: loadedPeriod2})
            } else {
                notify('Энергосфера не вернула профиль', 'warning', 4000)
            }
        } finally {
            setLoading(false)
        }
    }

    async function exportClick() {
        if (!excel) {
            notify('Нет данных для выгрузки', 'warning', 4000)
            return
        }
        await exportConsumptionExcel({
            ...excel,
            ndog: selectedDog?.ndog,
            subtitle: selectedDog?.name || '',
        })
    }

    function customizeTooltip(info) {
        const points = (info.points || []).filter((p) => p.seriesName !== FACT_MARK && p.seriesName !== PLAN_MARK)
        const valueLines = points.map((p) => `${p.seriesName}: ${p.valueText}`).join('<br/>')
        const marks = []
        if ((info.points || []).some((p) => p.seriesName === PLAN_MARK)) marks.push('Плановый час максимума')
        if ((info.points || []).some((p) => p.seriesName === FACT_MARK)) marks.push('Фактический час максимума')
        const arg = info.argument || info.argumentText || ''
        const day = slotParts(arg)?.dayKey
        const onHoliday = day && strips.some((band) => band.day === day)
        if (onHoliday) marks.unshift('Выходной')
        return {
            html: `<div><div>${arg}</div><div>${valueLines}</div>${marks.length ? `<div>${marks.join(', ')}</div>` : ''}</div>`,
        }
    }

    return (
        <Popup
            visible={visible}
            dragEnabled={true}
            hideOnOutsideClick={true}
            showCloseButton={true}
            showTitle={true}
            onHiding={() => {
                setData([])
                setStrips([])
                setExcel(null)
                if (onClosed) onClosed()
            }}
            maxWidth="1100px"
            width="95%"
            height="auto"
            titleRender={() => PopupTitleWithIcon('dx-icon-chart', 'Суммарный профиль по договору', onClosed)}
        >
            <div className="row" style={{alignItems: 'end'}}>
                <div className="col-lg-3 col-md-4 col-sm-6">
                    <DateBox
                        label="Период с"
                        labelMode="floating"
                        openOnFieldClick={true}
                        calendarOptions={calendarOptions}
                        value={period1}
                        max={period2}
                        displayFormat="dd.MM.yyyy"
                        useMaskBehavior={true}
                        onValueChanged={(e) => setPeriod1(e.value)}
                    />
                </div>
                <div className="col-lg-3 col-md-4 col-sm-6">
                    <DateBox
                        label="Период по"
                        labelMode="floating"
                        openOnFieldClick={true}
                        calendarOptions={calendarOptions}
                        value={period2}
                        min={period1}
                        displayFormat="dd.MM.yyyy"
                        useMaskBehavior={true}
                        onValueChanged={(e) => setPeriod2(e.value)}
                    />
                </div>
                <div className="col-lg-3 col-md-4 col-sm-6 mt20xs">
                    <Button icon="refresh" text="Обновить" className="w-100" onClick={refreshClick}/>
                </div>
                <div className="col-lg-3 col-md-4 col-sm-6 mt20xs">
                    <Button icon="xlsfile" text="Выгрузить в Excel" hint="Выгрузить в Excel" className="w-100"
                            onClick={exportClick} disabled={!excel || loading}/>
                </div>
            </div>
            <LoadPanel visible={loading}/>
            <Chart dataSource={data}>
                <Size height={420}/>
                <CommonSeriesSettings argumentField="dat_local" type="spline">
                    <Point visible={false}/>
                </CommonSeriesSettings>
                <Series valueField="value" name="Сумма"/>
                {hasPeakMark(data, PLAN_MARK) && (
                    <Series valueField={PLAN_MARK} name={PLAN_MARK} type="bar" color="#66bb6a" barWidth={2}/>
                )}
                {hasPeakMark(data, FACT_MARK) && (
                    <Series valueField={FACT_MARK} name={FACT_MARK} type="bar" color="#fbc02d" barWidth={2}/>
                )}
                <ArgumentAxis>
                    {strips.map((band) => (
                        <Strip key={band.day} startValue={band.start} endValue={band.end} color="rgba(255, 192, 203, 0.55)"/>
                    ))}
                </ArgumentAxis>
                <ValueAxis position="left">
                    <Title text="кВт·ч"/>
                </ValueAxis>
                <Legend visible={false}/>
                <Tooltip enabled={true} shared={true} customizeTooltip={customizeTooltip}/>
            </Chart>
            {data.length > 0 && <PeakMarksCaption/>}
        </Popup>
    )
}
